import { ThemeColors, ThemeMode, useColors } from "@/constants/theme";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useAppStore } from "@/stores/useStore";
import * as ImagePicker from "expo-image-picker";
import { ImagePlus, LogIn, LogOut, Moon, Settings, Smartphone, Sun, Trash2, X } from "lucide-react-native";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { Alert, Pressable, ScrollView, StyleSheet, View } from "react-native";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Divider,
  Dialog,
  Portal,
  Text,
  TextInput,
} from "react-native-paper";

const THEME_OPTIONS: {
  id: ThemeMode;
  label: string;
  icon: ComponentType<{ size: number; color: string }>;
}[] = [
  { id: "system", label: "端末に合わせる", icon: Smartphone },
  { id: "light", label: "ライト", icon: Sun },
  { id: "dark", label: "ダーク", icon: Moon },
];

export default function ProfileScreen() {
  const {
    currentUser,
    posts,
    reactions,
    login,
    signUp,
    logout,
    deleteAccount,
    updateProfile,
    loadCurrentUser,
    getCardUsageStats,
    cards,
    subjects,
    authLoading,
    authError,
    authMessage,
    themeMode,
    setThemeMode,
  } = useAppStore();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const myPosts = posts.filter(
    (p) => p.user_id === currentUser?.id && p.status !== "deleted",
  );
  const myPostIds = new Set(myPosts.map((post) => post.id));
  const stats = getCardUsageStats();
  const usedCardCount = new Set(stats.map((stat) => stat.card_id)).size;
  const usedSubjectCount = new Set(
    stats
      .map((stat) => cards.find((card) => card.id === stat.card_id)?.subject_id)
      .filter(Boolean),
  ).size;
  const receivedReactionsCount = reactions.filter((reaction) =>
    myPostIds.has(reaction.post_id),
  ).length;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlyPostsCount = myPosts.filter((post) => {
    const createdAt = new Date(post.created_at);
    return (
      createdAt.getFullYear() === currentYear &&
      createdAt.getMonth() === currentMonth
    );
  }).length;
  const monthlyPostTarget = 30;
  const cardTarget = Math.min(20, cards.length);
  const remainingSubjectCount = Math.max(subjects.length - usedSubjectCount, 0);
  const topThinkingType =
    stats
      .flatMap((stat) => {
        const card = cards.find((item) => item.id === stat.card_id);
        return (card?.thinking_tags ?? []).map((tag) => ({
          tag,
          count: stat.count,
        }));
      })
      .reduce<Record<string, number>>((acc, item) => {
        acc[item.tag] = (acc[item.tag] ?? 0) + item.count;
        return acc;
      }, {});
  const dominantThinkingType =
    Object.entries(topThinkingType).sort((a, b) => b[1] - a[1])[0]?.[0] ??
    "未分析";
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [grade, setGrade] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [bio, setBio] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setDisplayName(currentUser.display_name ?? "");
      setAvatarUrl(currentUser.avatar_url ?? "");
      setGrade(currentUser.grade ?? "");
      setAffiliation(currentUser.affiliation ?? "");
      setBio(currentUser.bio ?? "");
    }
  }, [currentUser]);

  useEffect(() => {
    loadCurrentUser().catch((error) => {
      console.warn("Failed to load current user", error);
    });
  }, [loadCurrentUser]);

  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      Alert.alert("入力が必要です", "名前は必須です。入力してください。");
      return;
    }

    try {
      await updateProfile({
        display_name: displayName.trim(),
        avatar_url: avatarUrl.trim() || undefined,
        grade: grade.trim() || undefined,
        affiliation: affiliation.trim() || undefined,
        bio: bio.trim() || undefined,
      });
      setEditMode(false);
    } catch (error) {
      console.warn("Failed to save profile", error);
      Alert.alert(
        "保存できませんでした",
        "通信状態を確認して、もう一度お試しください。",
      );
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmed) {
      Alert.alert("確認が必要です", "削除内容を確認してチェックを入れてください。");
      return;
    }

    try {
      await deleteAccount();
      setDeleteDialogVisible(false);
      setDeleteConfirmed(false);
      Alert.alert(
        "アカウントを削除しました",
        "アカウントと関連データの削除が完了しました。",
      );
    } catch (error) {
      console.warn("Failed to delete account", error);
      Alert.alert(
        "削除できませんでした",
        error instanceof Error
          ? error.message
          : "通信状態を確認して、もう一度お試しください。",
      );
    }
  };

  const pickAvatar = async () => {
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert(
          "写真へのアクセスが必要です",
          "端末の設定から写真ライブラリへのアクセスを許可してください。",
        );
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.85,
      });

      if (result.canceled) return;

      setAvatarUrl(result.assets[0]?.uri ?? "");
    } catch (error) {
      console.warn("Failed to pick avatar", error);
      Alert.alert(
        "画像を選択できませんでした",
        "時間をおいてもう一度お試しください。",
      );
    }
  };

  if (!currentUser) {
    return (
      <ScrollView style={styles.container}>
        <View style={styles.emptyContainer}>
          <Text variant="headlineMedium">ログインしていません</Text>
          <View style={styles.authForm}>
            <TextInput
              label="メールアドレス"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              mode="outlined"
              style={styles.input}
            />
            <TextInput
              label="パスワード"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              mode="outlined"
              style={styles.input}
            />
            <TextInput
              label="表示名（新規登録時）"
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.input}
            />
            {authError && <Text style={styles.authError}>{authError}</Text>}
            {authMessage && <Text style={styles.authMessage}>{authMessage}</Text>}
            <View style={styles.authActions}>
              <Button
                mode="contained"
                icon={() => <LogIn size={20} color="#fff" />}
                loading={authLoading}
                disabled={!email.trim() || !password || authLoading}
                onPress={() => {
                  login(email.trim(), password).catch((error) => {
                    console.warn("Failed to login", error);
                    Alert.alert("ログインできませんでした", "もう一度お試しください。");
                  });
                }}
                style={styles.authButton}
              >
                ログイン
              </Button>
              <Button
                mode="outlined"
                loading={authLoading}
                disabled={!email.trim() || !password || authLoading}
                onPress={() => {
                  signUp(email.trim(), password, displayName).catch((error) => {
                    console.warn("Failed to sign up", error);
                    Alert.alert("登録できませんでした", "もう一度お試しください。");
                  });
                }}
                style={styles.authButton}
              >
                新規登録
              </Button>
            </View>
          </View>
          {!isSupabaseConfigured && (
            <Button
              mode="text"
              icon={() => <LogIn size={20} color={c.primary} />}
              onPress={() => {
                login().catch((error) => {
                  console.warn("Failed to use demo login", error);
                  Alert.alert("ログインできませんでした", "もう一度お試しください。");
                });
              }}
              disabled={authLoading}
            >
              体験ログイン
            </Button>
          )}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.headerCard}>
        {currentUser.avatar_url ? (
          <Avatar.Image
            size={100}
            source={{ uri: currentUser.avatar_url }}
            style={styles.avatar}
          />
        ) : (
          <Avatar.Text
            size={100}
            label={currentUser.display_name
              .split(" ")
              .map((word) => word[0])
              .join("")
              .slice(0, 2)}
            style={styles.avatar}
          />
        )}
        <Text variant="headlineSmall" style={styles.name}>
          {currentUser.display_name}
        </Text>
        <Text variant="bodyMedium" style={styles.role}>
          {currentUser.role === "student" && "👤 学生"}
          {currentUser.role === "teacher" && "👨‍🏫 教員"}
          {currentUser.role === "admin" && "👨‍💼 管理者"}
        </Text>
        <Text variant="bodySmall" style={styles.affiliation}>
          {currentUser.grade || "学年未設定"}
          {currentUser.grade && currentUser.affiliation ? " • " : ""}
          {currentUser.affiliation || "学校・クラス未設定"}
        </Text>
      </View>

      <Card style={styles.card} mode="elevated">
        <Card.Title
          title="プロフィール"
          right={() => (
            <Button mode="text" onPress={() => setEditMode(!editMode)}>
              {editMode ? "閉じる" : "編集"}
            </Button>
          )}
        />
        <Card.Content>
          {editMode ? (
            <View style={styles.form}>
              <TextInput
                label="名前"
                value={displayName}
                onChangeText={setDisplayName}
                style={styles.input}
              />
              <View style={styles.avatarEditRow}>
                {avatarUrl ? (
                  <Avatar.Image size={64} source={{ uri: avatarUrl }} />
                ) : (
                  <Avatar.Text size={64} label={displayName.slice(0, 2) || "学"} />
                )}
                <View style={styles.avatarEditActions}>
                  <Pressable
                    accessibilityRole="button"
                    onPress={pickAvatar}
                    style={styles.avatarAction}
                  >
                    <ImagePlus size={18} color={c.primary} />
                    <Text style={styles.avatarActionText}>画像を選択</Text>
                  </Pressable>
                  {avatarUrl ? (
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => setAvatarUrl("")}
                      style={styles.avatarActionSecondary}
                    >
                      <X size={16} color={c.textSecondary} />
                      <Text style={styles.avatarActionSecondaryText}>削除</Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
              <TextInput
                label="学校・クラス（任意）"
                value={affiliation}
                onChangeText={setAffiliation}
                style={styles.input}
              />
              <TextInput
                label="学年・組（任意）"
                value={grade}
                onChangeText={setGrade}
                style={styles.input}
              />
              <TextInput
                label="ひとこと（任意）"
                value={bio}
                onChangeText={setBio}
                mode="outlined"
                multiline
                numberOfLines={3}
                style={styles.textArea}
              />
              <View style={styles.formActions}>
                <Button mode="outlined" onPress={() => setEditMode(false)}>
                  キャンセル
                </Button>
                <Button mode="contained" onPress={handleSaveProfile}>
                  保存
                </Button>
              </View>
            </View>
          ) : (
            <Text style={styles.bioText}>
              {currentUser.bio || "まだ自己紹介が設定されていません"}
            </Text>
          )}
        </Card.Content>
      </Card>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="📊 マイ統計" />
        <Card.Content>
          <View style={styles.statGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{myPosts.length}</Text>
              <Text style={styles.statLabel}>投稿</Text>
            </View>
            <Divider style={styles.verticalDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{usedCardCount}</Text>
              <Text style={styles.statLabel}>使用カード</Text>
            </View>
            <Divider style={styles.verticalDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{receivedReactionsCount}</Text>
              <Text style={styles.statLabel}>共感</Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="🎯 学習目標" />
        <Card.Content>
          <View style={styles.goalItem}>
            <Text style={styles.goalLabel}>今月の投稿目標</Text>
            <Text style={styles.goalValue}>
              {Math.min(monthlyPostsCount, monthlyPostTarget)} / {monthlyPostTarget}
            </Text>
          </View>
          <View style={styles.goalItem}>
            <Text style={styles.goalLabel}>使ったカード数</Text>
            <Text style={styles.goalValue}>
              {Math.min(usedCardCount, cardTarget)} / {cardTarget}
            </Text>
          </View>
          <View style={styles.goalItem}>
            <Text style={styles.goalLabel}>科目のバリエーション</Text>
            <Text style={styles.goalValue}>
              {usedSubjectCount} / {subjects.length}
            </Text>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="💡 次のステップ" />
        <Card.Content>
          <Text style={styles.actionText}>
            {remainingSubjectCount > 0
              ? `🏫 あと${remainingSubjectCount}科目を試すと、視点の幅がさらに広がります。`
              : "🏫 すべての科目を一度は試しています。次は深掘りしてみましょう。"}
          </Text>
          <Text style={styles.actionText}>
            {dominantThinkingType === "未分析"
              ? "✨ 投稿が増えると、よく使う思考タイプがここに表示されます。"
              : `✨ 得意な思考タイプは「${dominantThinkingType}」です。次は未使用のレンズで記録してみましょう。`}
          </Text>
        </Card.Content>
      </Card>

      <View style={styles.actionButtons}>
        <Button
          mode="outlined"
          icon={() => <Settings size={20} />}
          onPress={() => setEditMode(true)}
          style={styles.button}
        >
          プロフィール編集
        </Button>
        <Button
          mode="outlined"
          icon={() => <LogOut size={20} />}
          textColor={c.danger}
          onPress={() => {
            logout().catch((error) => {
              console.warn("Failed to logout", error);
              Alert.alert("ログアウトできませんでした", "もう一度お試しください。");
            });
          }}
          style={styles.button}
        >
          ログアウト
        </Button>
      </View>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="🎨 表示テーマ" />
        <Card.Content>
          <View style={styles.themeRow}>
            {THEME_OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = themeMode === option.id;
              return (
                <Pressable
                  key={option.id}
                  accessibilityRole="button"
                  onPress={() => setThemeMode(option.id)}
                  style={[styles.themeChip, active && styles.themeChipActive]}
                >
                  <Icon size={18} color={active ? c.primary : c.textMuted} />
                  <Text
                    style={[
                      styles.themeChipText,
                      active && styles.themeChipTextActive,
                    ]}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="アカウント管理" />
        <Card.Content>
          <Text style={styles.deleteDescription}>
            アカウントを削除すると、プロフィール、投稿、メモ、リアクションなどの関連データも削除されます。この操作は取り消せません。
          </Text>
          <Button
            mode="outlined"
            icon={() => <Trash2 size={20} color={c.danger} />}
            textColor={c.danger}
            onPress={() => setDeleteDialogVisible(true)}
            style={styles.deleteButton}
          >
            アカウント削除
          </Button>
        </Card.Content>
      </Card>

      <Portal>
        <Dialog
          visible={deleteDialogVisible}
          onDismiss={() => {
            if (authLoading) return;
            setDeleteDialogVisible(false);
            setDeleteConfirmed(false);
          }}
        >
          <Dialog.Title>アカウント削除</Dialog.Title>
          <Dialog.Content>
            <Text style={styles.dialogText}>
              アカウントと関連データを完全に削除します。この操作は取り消せません。
            </Text>
            <Pressable
              accessibilityRole="checkbox"
              accessibilityState={{ checked: deleteConfirmed }}
              onPress={() => setDeleteConfirmed((value) => !value)}
              style={styles.deleteConfirmRow}
            >
              <Checkbox
                status={deleteConfirmed ? "checked" : "unchecked"}
              />
              <Text style={styles.deleteConfirmText}>
                削除内容を理解しました
              </Text>
            </Pressable>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              onPress={() => {
                setDeleteDialogVisible(false);
                setDeleteConfirmed(false);
              }}
              disabled={authLoading}
            >
              キャンセル
            </Button>
            <Button
              mode="contained"
              buttonColor="#c53030"
              textColor="#fff"
              loading={authLoading}
              disabled={authLoading || !deleteConfirmed}
              onPress={handleDeleteAccount}
            >
              削除する
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <View style={{ height: 30 }} />
    </ScrollView>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.bg,
  },
  headerCard: {
    backgroundColor: c.surface,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: c.border,
  },
  avatar: {
    marginBottom: 16,
    borderWidth: 3,
    borderColor: c.primary,
  },
  name: {
    fontWeight: "700",
    marginBottom: 4,
    color: c.textPrimary,
  },
  role: {
    color: c.primary,
    fontWeight: "600",
    marginBottom: 8,
  },
  affiliation: {
    color: c.textMuted,
  },
  card: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 0,
    backgroundColor: c.surface,
  },
  bioText: {
    lineHeight: 22,
    color: c.textSecondary,
    fontStyle: "italic",
  },
  statGrid: {
    flexDirection: "row",
    justifyContent: "space-around",
    paddingVertical: 16,
  },
  statItem: {
    flex: 1,
    alignItems: "center",
  },
  statNumber: {
    fontSize: 28,
    fontWeight: "700",
    color: c.primary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: c.textSecondary,
  },
  verticalDivider: {
    width: 1,
    backgroundColor: c.border,
  },
  goalItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
  },
  goalLabel: {
    color: c.textSecondary,
    fontWeight: "600",
  },
  goalValue: {
    fontWeight: "700",
    color: c.primary,
  },
  actionText: {
    marginBottom: 10,
    lineHeight: 20,
    color: c.textSecondary,
  },
  form: {
    gap: 12,
    marginTop: 8,
  },
  input: {
    marginBottom: 8,
    backgroundColor: c.surfaceAlt,
  },
  textArea: {
    backgroundColor: c.surfaceAlt,
    marginBottom: 12,
  },
  avatarEditRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    marginBottom: 8,
  },
  avatarEditActions: {
    flex: 1,
    gap: 8,
  },
  avatarAction: {
    minHeight: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: c.primary,
    borderStyle: "dashed",
    backgroundColor: c.primarySoft,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  avatarActionText: {
    color: c.primaryDark,
    fontWeight: "700",
    fontSize: 13,
  },
  avatarActionSecondary: {
    minHeight: 36,
    borderRadius: 10,
    backgroundColor: c.surfaceAlt,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  avatarActionSecondaryText: {
    color: c.textSecondary,
    fontWeight: "700",
    fontSize: 12,
  },
  formActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 12,
    marginHorizontal: 16,
    marginTop: 16,
  },
  button: {
    flex: 1,
  },
  themeRow: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },
  themeChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  themeChipActive: {
    borderColor: c.primary,
    backgroundColor: c.primarySoft,
  },
  themeChipText: {
    color: c.textSecondary,
    fontWeight: "700",
    fontSize: 13,
  },
  themeChipTextActive: {
    color: c.primaryDark,
  },
  deleteDescription: {
    color: c.textSecondary,
    lineHeight: 20,
    marginBottom: 14,
  },
  deleteButton: {
    alignSelf: "flex-start",
    borderColor: c.danger,
  },
  dialogText: {
    color: c.textSecondary,
    lineHeight: 20,
    marginBottom: 12,
  },
  deleteConfirmRow: {
    minHeight: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surfaceAlt,
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  deleteConfirmText: {
    flex: 1,
    color: c.textPrimary,
    fontWeight: "600",
  },
  loginButton: {
    marginTop: 20,
  },
  authForm: {
    width: "100%",
    maxWidth: 360,
    marginTop: 24,
    gap: 10,
  },
  authActions: {
    flexDirection: "row",
    gap: 10,
    marginTop: 8,
  },
  authButton: {
    flex: 1,
  },
  authError: {
    color: c.danger,
    lineHeight: 20,
    fontSize: 12,
  },
  authMessage: {
    color: c.success,
    lineHeight: 20,
    fontSize: 12,
    backgroundColor: c.surfaceAlt,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
});
